import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import {useEffect, useRef, useState} from 'react';
import API, {getAPIBaseUrl} from '../../API';
import {useDispatch, useSelector} from 'react-redux';
import Ad, {SnigelAd} from '../../layout/Ad';
import {formatCount, formatTimeAgo} from '../../util/formatUtil';
import {getProfilePictureURL, UserBadge} from '../../util/userUtil';
import {Turnstile} from '@marsidev/react-turnstile';
import ListedVideo from '../../components/Video';
import {useRouter} from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Loader from '../../components/Loader';
import {getCookie} from 'cookies-next';
import Error from '../../components/Error';
import {getQualities, getThumbnailURL} from '../../util/videoUtil';
import {CommentFlags, UserFlags} from '../../shared/bitsets';
import {sortComments} from '../../shared/sorting';
import {I18nText} from '../../i18n/I18nText';

const MediaPlayer = dynamic(() => import('../../components/media/CHPlayer'), {ssr: false});

export default function ViewVideo({video, error}) {
    const user = useSelector((state) => state.user);
    const router = useRouter();
    const dispatch = useDispatch();
    const {id} = router.query;
    const [videoEngagement, setVideoEngagement] = useState([]);
    const [myRating, setMyRating] = useState(video?.my_rating);
    const engagement = useRef({data: [], lastSec: 0, secs: 0});

    useEffect(() => {
        dispatch({type: 'navbar:setActiveCategory', val: 'videos'});

        let engagementD = [];
        for (let i = 0; i < Math.ceil(video.length / 1000); i++) {
            engagementD[i] = 0;
        }
        engagement.current.data = engagementD;

        let sendEngagement = () => {
            // TODO: Respect DNT
            if (engagement.current.data.length === 0 || engagement.current.data.filter((i) => i !== 0).length === 0) return;

            const data = {
                data: engagement.current.data,
                viewtime: engagement.current.secs
            };

            let auth = getCookie('authentication');
            fetch(`${getAPIBaseUrl()}/video/${id}/engagement`, {
                method: 'POST',
                keepalive: true,
                headers: {
                    Authorization: auth ? `Bearer ${auth}` : undefined,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
        };

        async function fetchEngagement() {
            try {
                const response = await API.get(`/video/${id}/engagement`);

                setVideoEngagement(response.data.map((data) => {
                    return {data};
                }));
            } catch (e) {
                // Swallow
            }
        }

        async function confirmView() {
            try {
                await API.post(`/video/${id}/viewed`);
            } catch (e) {
                // Swallow
            }
        }

        fetchEngagement();
        confirmView();

        window.addEventListener('beforeunload', sendEngagement);
        return () => {
            window.removeEventListener('beforeunload', sendEngagement);
            sendEngagement();
        };
    }, [id, dispatch]);

    async function clickedLike(clickedRating) {
        if (user === undefined) {
            return;
        }
        if (user === null) {
            router.push('/login');
            return;
        }

        let translatedRating = clickedRating ? 1 : 0;
        let newRating;
        if (myRating === translatedRating) {
            newRating = null;
        } else {
            newRating = translatedRating;
        }
        setMyRating(newRating);

        if (newRating === null) {
            await API.delete(`/video/${id}/rating`);
        } else {
            await API.put(`/video/${id}/rating`, {
                rating: newRating
            });
        }
    }

    if (error) {
        return <Error error={error}/>;
    }

    let previousRating = video.my_rating;
    let likesAdd = previousRating === myRating ? 0 : (nullOrUndefined(previousRating) ? (myRating === 1 ? 1 : 0) : (previousRating === 1 ? -1 : (nullOrUndefined(myRating) ? 0 : 1)));
    let dislikesAdd = previousRating === myRating ? 0 : (nullOrUndefined(previousRating) ? (myRating === 0 ? 1 : 0) : (previousRating === 0 ? -1 : (nullOrUndefined(myRating) ? 0 : 1)));

    let likePercentage = 50;
    if (video.likes + likesAdd > 0 || video.dislikes + dislikesAdd > 0) {
        const total = video.likes + likesAdd + video.dislikes + dislikesAdd;
        likePercentage = Math.round(((video.likes + likesAdd) / total) * 100);
    }

    return <div className="p-3 max-w-1300px m-auto">
        <Head>
            <title>{`${video.title} - CornHub`}</title>

            <meta property="og:title" content={`${video.title} - CornHub`}/>
            <meta property="og:image" content={getThumbnailURL(video)}/>
            <meta property="og:type" content="video"/>
            <meta property="og:video"
                  content={`https://cornhub.objectdepot.net/video/${video.link_id}/${getQualities(video)[0]}.mp4`}/>
        </Head>

        <div className="grid grid-cols-1 md:grid-cols-10">
            <div className="md:col-span-7">
                <div className="w-full">
                    <MediaPlayer video={video} engagement={engagement} videoEngagement={videoEngagement}
                                 key={video.id}/>
                </div>
                <div>
                    <Ad orientation="horizontal" page="video" position="below-video" context={video.id}/>
                    <h1 className="text-2xl font-bold">{video.title}</h1>
                    <div className="flex">
                        <h3 className="w-full text-sm text-slate-400">
                            <I18nText id={video.views !== 1 ? 'video.view_count.plural' : 'video.view_count.single'}
                                      placeholders={{count: formatCount(video.views)}}/>
                            &nbsp;|&nbsp;
                            <img src="https://cornhub.objectdepot.net/static/icons/like.svg" width={12} height={12}
                                 className={`inline ml-1`} alt="Likes"/> {likePercentage}%
                            &nbsp;|&nbsp;
                            {formatTimeAgo(new Date(video.created_at))}
                        </h3>
                        <div className="grid items-right grid-flow-col auto-cols-auto w-max text-slate-400 gap-5 mr-1">
                            <button className="w-max" onClick={() => clickedLike(true)}>
                                <img
                                    src={myRating !== 1 ? 'https://cornhub.objectdepot.net/static/icons/like.svg' : 'https://cornhub.objectdepot.net/static/icons/liked.svg'}
                                    width={16} height={16} className={`inline ml-1 mr-2`}
                                    alt="Like"/> {formatCount(video.likes + likesAdd)}
                            </button>
                            <button className="w-max" onClick={() => clickedLike(false)}>
                                <img
                                    src={myRating !== 0 ? 'https://cornhub.objectdepot.net/static/icons/dislike.svg' : 'https://cornhub.objectdepot.net/static/icons/disliked.svg'}
                                    width={16} height={16} className={`inline ml-1 mr-2`}
                                    alt="Dislike"/> {formatCount(video.dislikes + dislikesAdd)}
                            </button>
                        </div>
                    </div>
                    <hr className="mt-2 mb-2 border-gray-700"/>
                    <div>
                        <Link href={`/model/${video.uploader.username}`} className="flex">
                            <img src={getProfilePictureURL(video.uploader)} alt="Avatar" width={64} height={64}
                                 className="rounded-full"/>
                            <div className="ml-3 inline-block m-auto">
                                <h2 className="text-xl">
                                    {video.uploader.display_name ?? video.uploader.username} <UserBadge
                                    user={video.uploader}
                                    className="inline"/>
                                </h2>
                                <h3 className="text-slate-400">
                                    <I18nText
                                        id={video.uploader.video_count !== 1 ? 'model.video_count.plural' : 'model.video_count.single'}
                                        placeholders={{count: formatCount(video.uploader.video_count)}}/>
                                </h3>
                            </div>
                        </Link>
                    </div>
                    <hr className="mt-2 mb-2 border-gray-700"/>
                    <CommentSection videoId={video.id}/>
                </div>
            </div>
            <div className="md:col-span-3">
                <div className="grid sm:grid-cols-2 ml-auto mr-auto md:block w-max">
                    <Ad orientation="square" page="video" position="besides-video" context={video.id}/>
                    <Ad orientation="square" page="video" position="besides-video" context={video.id}
                        housePlease={true}/>
                </div>
                <div className="ml-2">
                    <h3 className="text-lg font-bold"><I18nText id="video.recommended"/></h3>
                    <SidebarRecommendations video={video}/>
                </div>
            </div>
        </div>
    </div>;
}

ViewVideo.getInitialProps = async ({req, res = {}, query}) => {
    const {id} = query;
    const {data, errored, status} = await API.get(`/video/${id}`, {req});

    if (errored) {
        if (status === 404) {
            res.statusCode = 404;
            return {error: {status: 404, message: '404 - Video Not Found'}};
        }

        res.statusCode = 503;
        return {error: {status: 503}};
    }

    return {video: data};
}

function CommentSection({
                            videoId
                        }) {
    const [comments, setComments] = useState();
    const [commentBoxShownFor, setCommentBoxShownFor] = useState(undefined);
    const [refreshId, setRefreshId] = useState(0);
    const refresh = () => setRefreshId(refreshId + 1);

    useEffect(() => {
        async function fetchComments() {
            const response = await API.get(`/video/${videoId}/comments`);
            let comments = response.data;

            comments.sort(sortComments);
            const sortChildren = (c) => {
                c.sort(sortComments);
                c.forEach((cc) => sortChildren(cc.children));
            }
            comments.forEach((cc) => sortChildren(cc.children));

            setComments(comments);
            setCommentBoxShownFor(undefined);
        }

        fetchComments();
    }, [videoId, refreshId]);

    if (!comments) {
        return <Loader/>;
    }

    const countChildren = (total, comment) => total + comment.children.reduce(countChildren, comment.children.length);

    return <>
        <h2 className="text-xl">
            <I18nText id="video.comments"
                      placeholders={{count: comments.reduce(countChildren, comments.length)}}/>
        </h2>
        <CommentBox videoId={videoId} refresh={refresh}/>
        <div>
            <SnigelAd ad={{adnginId: 'incontent_2'}} orientation="parent"/>
            {comments.map((comment) => <Comment key={comment.id} comment={comment}
                                                commentBoxShownFor={commentBoxShownFor} refresh={refresh}
                                                setCommentBoxShownFor={setCommentBoxShownFor}/>)}
        </div>
    </>;
}

function Comment({comment, depth = 0, commentBoxShownFor, setCommentBoxShownFor, refresh}) {
    const [myRating, setMyRating] = useState(comment.my_rating);
    const user = useSelector((state) => state.user);
    const router = useRouter();

    async function clickedLike(clickedRating) {
        if (user === undefined) {
            return;
        }
        if (user === null) {
            router.push('/login');
            return;
        }

        let translatedRating = clickedRating ? 1 : 0;
        let newRating;
        if (myRating === translatedRating) {
            newRating = null;
        } else {
            newRating = translatedRating;
        }
        setMyRating(newRating);

        if (newRating === null) {
            await API.delete(`/video/${comment.video_id}/comment/${comment.id}/rating`);
        } else {
            await API.put(`/video/${comment.video_id}/comment/${comment.id}/rating`, {
                rating: newRating
            });
        }
    }

    async function deleteComment() {
        await API.delete(`/video/${comment.video_id}/comment/${comment.id}`);

        refresh();
    }

    async function restoreComment() {
        await API.post(`/video/${comment.video_id}/comment/${comment.id}/restore`);

        refresh();
    }

    let previousRating = comment.my_rating;
    let likesAdd = previousRating === myRating ? 0 : (nullOrUndefined(previousRating) ? (myRating === 1 ? 1 : 0) : (previousRating === 1 ? -1 : (nullOrUndefined(myRating) ? 0 : 1)));
    let dislikesAdd = previousRating === myRating ? 0 : (nullOrUndefined(previousRating) ? (myRating === 0 ? 1 : 0) : (previousRating === 0 ? -1 : (nullOrUndefined(myRating) ? 0 : 1)));

    return <div
        className={depth !== 0 && depth <= 5 ? 'ml-8 pl-2' : `pl-0 ${depth === 0 ? 'pt-[8px] pb-[8px]' : ''}`}>
        <div className={`${(comment.flags & CommentFlags.Deleted) !== 0 ? 'bg-red-500 bg-opacity-20' : ''} p-2`}>
            <div className="flex">
                <img src={getProfilePictureURL(comment.commenter)} width={32} height={32} alt="Avatar"
                     className="rounded-full h-max"/>
                <Link className="ml-1 text-primary m-auto text-lg font-semibold overflow-auto"
                      href={`/model/${comment.commenter.username}`}>
                    {comment.commenter.display_name ?? comment.commenter.username}
                    <UserBadge user={comment.commenter} className="inline ml-1"/>
                    <span className="text-slate-400 text-xs font-normal ml-1">{formatTimeAgo(comment.created_at)}</span>
                </Link>
            </div>
            <p className="break-all overflow-auto">{comment.comment}</p>
            <div>
                {comment.id ? <>
                    <button onClick={() => clickedLike(true)}>
                        <img
                            src={myRating !== 1 ? 'https://cornhub.objectdepot.net/static/icons/like.svg' : 'https://cornhub.objectdepot.net/static/icons/liked.svg'}
                            width={16} height={16} className={`inline ml-1 mr-1`}
                            alt="Like"/> {formatCount(comment.likes + likesAdd)}
                    </button>
                    <button className="ml-2 mr-2" onClick={() => clickedLike(false)}>
                        <img
                            src={myRating !== 0 ? 'https://cornhub.objectdepot.net/static/icons/dislike.svg' : 'https://cornhub.objectdepot.net/static/icons/disliked.svg'}
                            width={16} height={16} className={`inline ml-1 mr-1`}
                            alt="Dislike"/> {formatCount(comment.dislikes + dislikesAdd)}
                    </button>
                    <span className="font-bold">&#x2022;</span>
                    <button className="ml-2 mr-2 font-bold"
                            onClick={() => setCommentBoxShownFor(commentBoxShownFor !== comment.id ? comment.id : undefined)}>Reply
                    </button>
                    {user && (user.flags & UserFlags.Staff) !== 0 ? <>
                        <span className="font-bold">&#x2022;</span>
                        {(comment.flags & CommentFlags.Deleted) !== 0 ?
                            <button className="ml-2 text-green-500 font-bold underline"
                                    onClick={() => restoreComment()}>
                                <I18nText id="video.comments.moderation.restore"/>
                            </button> :
                            <button className="ml-2 text-red-500 font-bold underline" onClick={() => deleteComment()}>
                                <I18nText id="video.comments.moderation.delete"/>
                            </button>}
                    </> : null}
                </> : null}
            </div>
            {commentBoxShownFor === comment.id ?
                <CommentBox videoId={comment.video_id} refresh={refresh} replyComment={comment}/> : null}
        </div>
        <div>
            {comment.children.map((child) => <Comment key={child.id} comment={child} depth={depth + 1}
                                                      refresh={refresh} commentBoxShownFor={commentBoxShownFor}
                                                      setCommentBoxShownFor={setCommentBoxShownFor}/>)}
        </div>
    </div>;
}

function CommentBox({videoId, replyComment = null, refresh}) {
    const [comment, setComment] = useState('');
    const turnstile = useRef(null);
    const [turnstileToken, setTurnstileToken] = useState();
    const user = useSelector((state) => state.user);
    const router = useRouter();

    if (!user) {
        if (replyComment) {
            router.push('/login');
        }
        return null;
    }

    async function postComment() {
        if (comment.length < 1 || comment.length > 2048 || !turnstileToken) return;

        setComment('');

        await API.post(`/video/${videoId}/comment`, {
            comment, reply_to: replyComment !== null ? replyComment.id : null, turnstile: turnstileToken
        });

        setTurnstileToken(undefined);
        turnstile.current?.reset();

        refresh();
    }

    return <div className="overflow-auto mt-3">
        <div className="flex">
            <img src={getProfilePictureURL(user)} width={48} height={48} alt="Avatar" className="rounded-full h-full"/>
            <div className="w-full comment-box ml-5 relative">
                <textarea className="rounded-none resize-none text-black w-full p-1 z-10" rows={4}
                          placeholder="Share what you think"
                          maxLength={2048}
                          onChange={(e) => setComment(e.target.value)} value={comment}/>
            </div>
        </div>

        {turnstileToken === null ?
            <p className="float-left text-red-500"><I18nText id="turnstile.verification_failed"/></p> : null}
        {turnstileToken === undefined ?
            <p className="float-left text-blue-500"><I18nText id="turnstile.verifying"/></p> : null}

        <Turnstile siteKey="0x4AAAAAAABzV2tD73ijh26J" onSuccess={(token) => setTurnstileToken(token)}
                   onError={() => setTurnstileToken(null)} options={{action: 'comment', size: 'invisible'}}
                   ref={turnstile}/>
        <button
            className={`p-2 bg-gray-800 hover:bg-gray-700 active:bg-gray-600 disabled:bg-gray-500 rounded-sm m-1 float-right transition`}
            disabled={comment === '' || !turnstileToken} onClick={() => postComment()}>
            <I18nText id="video.comments.create"/>
        </button>
    </div>;
}

function SidebarRecommendations({video}) {
    const [recommended, setRecommended] = useState();

    useEffect(() => {
        async function fetchRecommended() {
            const response = await API.get(`/recommendation/sidebar/${video.id}`);
            setRecommended(response.data);
        }

        fetchRecommended();
    }, [video.id]);

    if (!recommended) {
        return [0, 1, 2, 3, 4].map((i) => <div className="animate-pulse flex" key={i}>
            <div className="w-4/7 mt-1 bg-gray-900 thumbnail-aspect-ratio"/>
            <div className="w-3/7 ml-1">
                <div className="mt-2 bg-gray-700 h-1/8"/>
                <div className="mt-1 bg-gray-700 h-1/8"/>
                <div className="w-1/2 mt-1 bg-gray-800 h-1/8"/>
            </div>
        </div>);
    }

    return recommended.map((video) => <ListedVideo video={video} inList={true} key={video.id}/>);
}

function nullOrUndefined(val) {
    return val === null || val === undefined;
}
